// Xero — weekly bill export/post (REQ-xero-integration frontend gap).
//
// The backend (xero_adapter.py, mvp_surfaces_api.py) has been built and
// tested since before this UI existed: it groups approved invoices by
// supplier into GL-categorized ACCPAY bills and either (a) posts them to
// Xero as DRAFT bills, or (b) exports the same aggregation as a CSV a
// bookkeeper can import by hand. This screen is the only client for both.
//
// Endpoints used:
//   GET  /api/platform/entities?organization_id=  — which Xero tenant(s)
//        this org can post as (a group's child entities, or the org itself
//        for a flat single-tenant org — see server.py:list_org_entities).
//   POST /api/xero/post-now?entity_id=&week=      — org_owner/org_admin only.
//        Idempotent per (org, entity, week, supplier) — re-posting a week
//        already posted returns it under `skipped`, not a duplicate bill.
//        Response includes `demo_mode`: true whenever the organisation has no
//        Xero connection, so FakeXeroClient ran and NOTHING left the box —
//        this must be surfaced, never silently treated the same as a real
//        post (xero_connection.get_xero_client_for). 409 xero_not_connected
//        when the org is connected but this entity is not linked yet.
//   GET  /api/xero/export.csv?entity_id=&week=    — org_owner/org_admin/manager.
//        Manual-import-compatible CSV fallback; works with zero Xero setup.
//   /api/integrations/xero/{status,connect,pending,link,disconnect} —
//        org_owner/org_admin only. Per-entity Xero connection (OAuth). The
//        Connect section is shown only when /status answers (a 403 hides it);
//        Xero redirects back here with ?xero=connected|choose|error.
//
// Not role-hidden: like Documents.js's delete button, both actions are shown
// to any authenticated user on this screen and the backend's require_role is
// the sole authority — a denial surfaces as the same honest apiErrorMessage
// banner every other screen in this app uses for a 403.
import React, { useState, useEffect, useCallback } from 'react';
import { Landmark, Download, Send, Loader2, AlertTriangle, CheckCircle2, Info, Link2, Unlink } from 'lucide-react';
import { apiErrorMessage } from './utils/apiError';
import { authHeaders, apiBase, resolveOrgId } from './utils/authHeaders';

const API_URL = apiBase();
const XERO_CONN = `${API_URL}/api/integrations/xero`;

const XERO_ERROR_REASONS = {
  invalid_state: 'The Xero sign-in link expired or was already used. Start again.',
  access_denied: 'Access was not granted in Xero.',
  no_tenants: 'That Xero login has no organisation to connect.',
  xero_app_not_configured: 'The Xero app is not configured on this server.',
};

/**
 * Outcome of a Xero OAuth redirect back to this page (?xero=connected|choose|error).
 * Returns null when the URL carries no Xero outcome.
 */
export function parseXeroOutcome(search) {
  const params = new URLSearchParams(search || '');
  const outcome = params.get('xero');
  if (!['connected', 'choose', 'error'].includes(outcome)) return null;
  const entityId = params.get('entity_id') || null;
  const reason = params.get('reason') || null;
  let message;
  if (outcome === 'connected') message = 'Xero connected.';
  else if (outcome === 'choose') message = 'Your Xero login can reach several organisations — choose the one for this entity.';
  else message = XERO_ERROR_REASONS[reason] || 'Connecting Xero failed. Please try again.';
  return { outcome, entityId, reason, message };
}

/** One-line connection summary for an entity row from GET /api/integrations/xero/status. */
export function describeConnection(row) {
  if (!row) return { text: 'Not connected', linked: false, needsAttention: false };
  if (row.linked) {
    if (row.last_refresh_error) {
      return { text: `Reconnect needed — Xero refused the token refresh (${row.last_refresh_error})`, linked: true, needsAttention: true };
    }
    return { text: `Connected to ${row.xero_tenant_name || 'Xero'}`, linked: true, needsAttention: false };
  }
  if (row.pending_choice) return { text: 'Choose a Xero organisation to finish connecting', linked: false, needsAttention: true };
  return { text: 'Not connected', linked: false, needsAttention: false };
}

/** Monday of the week containing `date`, as YYYY-MM-DD ("week commencing"). */
export function mondayOf(date) {
  const d = new Date(date.getTime());
  const day = d.getDay(); // 0=Sun..6=Sat
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/**
 * Which entity_id should be sent, given the discovered entities and whatever
 * the user has explicitly picked so far.
 *
 * A single-entity org (the common case — a flat tenant with no group
 * hierarchy) is auto-selected: there is no real choice to make. A
 * multi-entity group returns null until the caller picks one — silently
 * defaulting to "the first entity in the list" would post a real DRAFT bill
 * (or export a CSV) under the wrong legal entity's VAT number.
 */
export function resolveEntitySelection(entities, selectedId) {
  if (!Array.isArray(entities) || entities.length === 0) return null;
  if (entities.length === 1) return entities[0].id;
  return entities.find((e) => e.id === selectedId) ? selectedId : null;
}

/** Human summary of a post-now response, and whether it actually reached Xero. */
export function describePostResult(result) {
  if (!result) return { text: '', isDemoMode: false };
  const postedN = (result.posted || []).length;
  const skippedN = (result.skipped || []).length;
  const parts = [];
  if (postedN) parts.push(`${postedN} bill${postedN === 1 ? '' : 's'} posted as DRAFT`);
  if (skippedN) parts.push(`${skippedN} already posted this week (skipped)`);
  if (!postedN && !skippedN) parts.push('No approved invoices to post this week');
  return { text: parts.join(' · '), isDemoMode: Boolean(result.demo_mode) };
}

const Xero = () => {
  const [entities, setEntities] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [entitiesError, setEntitiesError] = useState(null);
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [week, setWeek] = useState(() => mondayOf(new Date()));

  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState(null);

  const [postBusy, setPostBusy] = useState(false);
  const [postError, setPostError] = useState(null);
  const [postResult, setPostResult] = useState(null);

  // Per-entity Xero connection (org_owner/org_admin only; hidden on 403).
  const [conn, setConn] = useState(null);
  const [connHidden, setConnHidden] = useState(false);
  const [connError, setConnError] = useState(null);
  const [connBusy, setConnBusy] = useState(null); // entity id with a request in flight
  const [outcome, setOutcome] = useState(null);
  const [chooser, setChooser] = useState(null); // { entityId, tenants }

  const loadConnection = useCallback(async () => {
    try {
      const res = await fetch(`${XERO_CONN}/status`, { headers: authHeaders() });
      if (res.status === 403 || res.status === 401) { setConnHidden(true); return; }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setConnError(apiErrorMessage(err.detail, `Could not load Xero connection status (${res.status})`));
        return;
      }
      setConnError(null);
      setConn(await res.json());
    } catch {
      setConnError('Could not load Xero connection status.');
    }
  }, []);

  const loadPending = useCallback(async (entityId) => {
    try {
      const res = await fetch(`${XERO_CONN}/pending?entity_id=${encodeURIComponent(entityId)}`, { headers: authHeaders() });
      if (!res.ok) return;
      const body = await res.json();
      if (Array.isArray(body.tenants) && body.tenants.length) setChooser({ entityId, tenants: body.tenants });
    } catch { /* the status row still says a choice is pending */ }
  }, []);

  useEffect(() => {
    const parsed = parseXeroOutcome(window.location.search);
    if (parsed) {
      setOutcome(parsed);
      if (parsed.outcome === 'choose' && parsed.entityId) loadPending(parsed.entityId);
      // Drop the outcome params so a refresh does not replay the banner.
      const url = new URL(window.location.href);
      ['xero', 'entity_id', 'reason'].forEach((k) => url.searchParams.delete(k));
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    }
    loadConnection();
  }, [loadConnection, loadPending]);

  const connectEntity = async (entityId) => {
    setConnBusy(entityId);
    setConnError(null);
    try {
      const res = await fetch(`${XERO_CONN}/connect`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_id: entityId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.authorize_url) {
        setConnError(apiErrorMessage(body.detail, `Could not start the Xero connection (${res.status})`));
        setConnBusy(null);
        return;
      }
      window.location.assign(body.authorize_url);
    } catch {
      setConnError('Could not start the Xero connection.');
      setConnBusy(null);
    }
  };

  const linkTenant = async (entityId, xeroTenantId) => {
    setConnBusy(entityId);
    setConnError(null);
    try {
      const res = await fetch(`${XERO_CONN}/link`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_id: entityId, xero_tenant_id: xeroTenantId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setConnError(apiErrorMessage(err.detail, `Could not link that Xero organisation (${res.status})`));
        return;
      }
      setChooser(null);
      setOutcome({ outcome: 'connected', entityId, reason: null, message: 'Xero connected.' });
      await loadConnection();
    } catch {
      setConnError('Could not link that Xero organisation.');
    } finally {
      setConnBusy(null);
    }
  };

  const disconnectEntity = async (row) => {
    if (!window.confirm(`Disconnect ${row.entity_name || row.entity_id} from ${row.xero_tenant_name || 'Xero'}?`)) return;
    setConnBusy(row.entity_id);
    setConnError(null);
    try {
      const res = await fetch(`${XERO_CONN}/disconnect`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_id: row.entity_id }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setConnError(apiErrorMessage(err.detail, `Could not disconnect (${res.status})`));
        return;
      }
      await loadConnection();
    } catch {
      setConnError('Could not disconnect.');
    } finally {
      setConnBusy(null);
    }
  };

  const loadEntities = useCallback(async () => {
    const orgId = resolveOrgId();
    if (!orgId) { setEntitiesLoading(false); return; }
    setEntitiesLoading(true);
    setEntitiesError(null);
    try {
      const res = await fetch(
        `${API_URL}/api/platform/entities?organization_id=${encodeURIComponent(orgId)}`,
        { headers: authHeaders() },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setEntitiesError(apiErrorMessage(err.detail, `Could not load entities (${res.status})`));
        setEntities([]);
        return;
      }
      const body = await res.json();
      const list = Array.isArray(body) ? body : [];
      setEntities(list);
      if (list.length === 1) setSelectedEntityId(list[0].id);
    } catch {
      setEntitiesError('Could not load entities.');
      setEntities([]);
    } finally {
      setEntitiesLoading(false);
    }
  }, []);

  useEffect(() => { loadEntities(); }, [loadEntities]);

  const entityId = resolveEntitySelection(entities, selectedEntityId);
  const ready = Boolean(entityId && week);

  const exportCsv = async () => {
    if (!ready) return;
    setCsvBusy(true);
    setCsvError(null);
    try {
      const res = await fetch(
        `${API_URL}/api/xero/export.csv?entity_id=${encodeURIComponent(entityId)}&week=${encodeURIComponent(week)}`,
        { headers: authHeaders() },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setCsvError(apiErrorMessage(err.detail, `Export refused (${res.status})`));
        return;
      }
      const text = await res.text();
      const blob = new Blob([text], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `xero-bills-${week}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setCsvError('Export failed.');
    } finally {
      setCsvBusy(false);
    }
  };

  const postNow = async () => {
    if (!ready) return;
    if (!window.confirm(
      `Post this week's approved invoices to Xero as DRAFT bills for ${
        entities.find((e) => e.id === entityId)?.name || entityId
      }, week commencing ${week}?`
    )) {
      return;
    }
    setPostBusy(true);
    setPostError(null);
    setPostResult(null);
    try {
      const res = await fetch(
        `${API_URL}/api/xero/post-now?entity_id=${encodeURIComponent(entityId)}&week=${encodeURIComponent(week)}`,
        { method: 'POST', headers: authHeaders() },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setPostError(apiErrorMessage(err.detail, `Post refused (${res.status})`));
        return;
      }
      const body = await res.json();
      setPostResult(body);
    } catch {
      setPostError('Post failed.');
    } finally {
      setPostBusy(false);
    }
  };

  const summary = describePostResult(postResult);

  return (
    <div className="min-h-screen bg-surface-muted" data-testid="xero-page">
      <div className="max-w-screen-lg mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-overline text-ink-muted">Stay Compliant</div>
          <h1 className="font-display text-3xl text-ink mt-1 flex items-center gap-2">
            <Landmark className="w-7 h-7 text-primary-500" />
            Xero Export
          </h1>
          <p className="text-ink-muted text-sm mt-1">
            Post this week&apos;s approved invoices to Xero as DRAFT bills — grouped by supplier, split
            by GL category with VAT — or export the same aggregation as a CSV you can import by hand.
          </p>
        </div>

        {outcome ? (
          <div
            className={`mb-4 text-sm rounded-md px-3 py-2 flex items-start gap-2 ${
              outcome.outcome === 'error' ? 'bg-danger/10 text-danger border border-danger/30'
                : outcome.outcome === 'choose' ? 'bg-warn/10 text-warn border border-warn/30'
                  : 'bg-ok/10 text-ok border border-ok/30'
            }`}
            data-testid="xero-connect-outcome"
          >
            {outcome.outcome === 'error' ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> : <Info className="w-4 h-4 mt-0.5 shrink-0" />}
            <span>{outcome.message}</span>
          </div>
        ) : null}

        {!connHidden && (conn || connError) ? (
          <div className="bg-surface border border-surface-border rounded-lg p-5 mb-6" data-testid="xero-connection">
            <div className="text-xs uppercase tracking-overline text-ink-muted mb-2">Xero connection</div>
            {conn && !conn.app_configured ? (
              <div className="text-sm text-ink-muted mb-2" data-testid="xero-app-not-configured">
                The Xero app is not configured on this server, so entities cannot be connected yet.
              </div>
            ) : null}
            {connError ? (
              <div className="text-xs text-danger flex items-center gap-1.5 mb-2" data-testid="xero-connection-error">
                <AlertTriangle className="w-3.5 h-3.5" /> {connError}
              </div>
            ) : null}
            <ul className="divide-y divide-surface-border">
              {((conn && conn.entities) || []).map((row) => {
                const d = describeConnection(row);
                const busy = connBusy === row.entity_id;
                return (
                  <li key={row.entity_id} className="py-2 flex items-center justify-between gap-3 flex-wrap" data-testid={`xero-conn-row-${row.entity_id}`}>
                    <div>
                      <div className="text-sm text-ink">{row.entity_name || row.entity_id}</div>
                      <div className={`text-xs ${d.needsAttention ? 'text-warn' : 'text-ink-muted'}`}>{d.text}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!d.linked || d.needsAttention ? (
                        <button
                          disabled={busy || !conn.app_configured}
                          onClick={() => connectEntity(row.entity_id)}
                          className="px-3 py-1.5 bg-primary-500 text-surface rounded-md text-xs hover:bg-primary-600 disabled:opacity-50 inline-flex items-center gap-1.5"
                          data-testid={`xero-connect-btn-${row.entity_id}`}
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                          {d.linked ? 'Reconnect' : 'Connect Xero'}
                        </button>
                      ) : null}
                      {d.linked ? (
                        <button
                          disabled={busy}
                          onClick={() => disconnectEntity(row)}
                          className="px-3 py-1.5 bg-surface-sunken text-ink rounded-md text-xs hover:bg-danger/10 hover:text-danger disabled:opacity-50 inline-flex items-center gap-1.5"
                          data-testid={`xero-disconnect-btn-${row.entity_id}`}
                        >
                          <Unlink className="w-3.5 h-3.5" /> Disconnect
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
            {chooser ? (
              <div className="mt-3 border-t border-surface-border pt-3" data-testid="xero-tenant-chooser">
                <div className="text-sm text-ink mb-2">
                  Which Xero organisation should {chooser.entityId} post to?
                </div>
                <div className="flex flex-wrap gap-2">
                  {chooser.tenants.map((t) => (
                    <button
                      key={t.xero_tenant_id}
                      disabled={connBusy === chooser.entityId}
                      onClick={() => linkTenant(chooser.entityId, t.xero_tenant_id)}
                      className="px-3 py-1.5 bg-surface-sunken text-ink rounded-md text-xs hover:bg-primary-50 hover:text-primary-700 disabled:opacity-50"
                      data-testid={`xero-tenant-option-${t.xero_tenant_id}`}
                    >
                      {t.xero_tenant_name || t.xero_tenant_id}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="bg-surface border border-surface-border rounded-lg p-5 mb-6" data-testid="xero-controls">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-overline text-ink-muted">Entity (Xero tenant)</label>
              {entitiesLoading ? (
                <div className="mt-1 text-sm text-ink-muted flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : entitiesError ? (
                <div className="mt-1 text-sm text-danger flex items-center gap-1.5" data-testid="xero-entities-error">
                  <AlertTriangle className="w-3.5 h-3.5" /> {entitiesError}
                </div>
              ) : entities.length === 0 ? (
                <div className="mt-1 text-sm text-ink-muted">No entity found for this organization.</div>
              ) : entities.length === 1 ? (
                <div className="mt-1 px-3 py-2 bg-surface-sunken rounded-md text-sm text-ink" data-testid="xero-entity-single">
                  {entities[0].name || entities[0].id}
                </div>
              ) : (
                <select
                  aria-label="Entity"
                  value={selectedEntityId}
                  onChange={(e) => setSelectedEntityId(e.target.value)}
                  className="block w-full mt-1 px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm focus:outline-none focus:border-primary-500"
                  data-testid="xero-entity-select"
                >
                  <option value="">Choose an entity…</option>
                  {entities.map((e) => (
                    <option key={e.id} value={e.id}>{e.name || e.id}</option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-overline text-ink-muted">Week commencing</label>
              <input
                aria-label="Week commencing"
                type="date"
                value={week}
                onChange={(e) => setWeek(e.target.value)}
                className="block w-full mt-1 px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm focus:outline-none focus:border-primary-500"
                data-testid="xero-week-input"
              />
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <button
              disabled={!ready || csvBusy}
              onClick={exportCsv}
              className="px-4 py-2 bg-surface-sunken text-ink rounded-md text-sm hover:bg-primary-50 hover:text-primary-700 disabled:opacity-50 motion-organic inline-flex items-center gap-1.5"
              data-testid="xero-export-csv-btn"
            >
              {csvBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </button>
            <button
              disabled={!ready || postBusy}
              onClick={postNow}
              className="px-4 py-2 bg-primary-500 text-surface rounded-md text-sm hover:bg-primary-600 disabled:opacity-50 motion-organic inline-flex items-center gap-1.5"
              data-testid="xero-post-now-btn"
            >
              {postBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Post to Xero (as draft)
            </button>
          </div>

          {csvError ? (
            <div className="mt-3 text-xs text-danger flex items-center gap-1.5" data-testid="xero-csv-error">
              <AlertTriangle className="w-3.5 h-3.5" /> {csvError}
            </div>
          ) : null}
          {postError ? (
            <div className="mt-3 text-xs text-danger flex items-center gap-1.5" data-testid="xero-post-error">
              <AlertTriangle className="w-3.5 h-3.5" /> {postError}
            </div>
          ) : null}
          {postResult ? (
            <div
              className={`mt-3 text-sm rounded-md px-3 py-2 flex items-start gap-2 ${
                summary.isDemoMode
                  ? 'bg-warn/10 text-warn border border-warn/30'
                  : 'bg-ok/10 text-ok border border-ok/30'
              }`}
              data-testid="xero-post-result"
            >
              {summary.isDemoMode ? <Info className="w-4 h-4 mt-0.5 shrink-0" /> : <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />}
              <div>
                <div className="font-medium" data-testid="xero-post-summary">{summary.text}</div>
                {summary.isDemoMode ? (
                  <div className="mt-0.5" data-testid="xero-demo-mode-banner">
                    Demo mode — this organisation has no Xero connection, so nothing was actually sent
                    to Xero. An owner or admin can connect Xero above to post for real.
                  </div>
                ) : (
                  <div className="mt-0.5">
                    Bills landed in Xero as <strong>DRAFT</strong> — review and approve them there before
                    they affect your accounts.
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        <p className="text-xs text-ink-subtle flex items-center gap-1.5">
          <Info className="w-3 h-3" />
          Posting is idempotent per entity/week/supplier — re-posting a week already sent to Xero skips
          it rather than duplicating the bill.
        </p>
      </div>
    </div>
  );
};

export default Xero;
